import React, { useEffect, useState } from "react";
import { Path, MapPinLine, Lock, CheckCircle, Info, Sparkle } from "@phosphor-icons/react";
import api from "../api";

const STAGES = ["Discovery Access", "Explorer Rate", "Standard Access", "Sponsored Frontier"];

const StageBadge = ({ active, label }) => (
  <div
    data-testid={`journey-stage-${label.toLowerCase().replace(/\s+/g, "-")}`}
    className={`rounded-md border px-3 py-2 text-center text-xs transition-colors ${
      active ? "border-ff-gold bg-ff-gold/10 text-ff-gold" : "border-ff-brass/20 text-ff-muted"
    }`}
  >
    {label}
  </div>
);

export default function Journey() {
  React.useEffect(() => { import("../api").then((m) => m.track("journey_page_view")); }, []);
  const [j, setJ] = useState(null);

  useEffect(() => {
    api.get("/journey").then((r) => setJ(r.data)).catch(() => {});
  }, []);

  if (!j) return <div className="ff-panel rounded-md p-8 text-center text-ff-muted">Loading your journey…</div>;

  const total = j.journey_total;
  const pct = Math.min(100, Math.round((j.campaigns_completed / total) * 100));

  return (
    <div className="space-y-6" data-testid="journey-page">
      <div className="flex flex-wrap items-center gap-3">
        <Path size={26} weight="duotone" className="text-ff-gold" />
        <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Explorer Journey</h1>
        <span className="inline-flex items-center gap-1 rounded-full border border-ff-brass/40 px-3 py-1 text-xs uppercase tracking-widest text-ff-brass">
          <Info size={13} /> Preview · Nothing charged
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="journey-stages">
        {STAGES.map((st) => <StageBadge key={st} label={st} active={st === j.current_stage} />)}
      </div>

      <div className="ff-panel rounded-md p-5" data-testid="journey-progress">
        <div className="flex items-baseline justify-between">
          <span className="text-sm text-ff-muted">Current stage</span>
          <span className="font-display text-lg font-bold text-ff-gold" data-testid="journey-current-stage">{j.current_stage}</span>
        </div>
        <p className="mt-3 text-sm text-ff-parchment/90" data-testid="journey-progress-text">{j.messages?.progress}</p>
        <div className="mt-2 h-3 w-full overflow-hidden rounded-full bg-ff-bg">
          <div className="h-full rounded-full bg-ff-gold transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <div className="ff-panel rounded-md p-4" data-testid="journey-discovery">
          <div className="text-xs uppercase tracking-widest text-ff-brass">Discovery (Free)</div>
          <div className="mt-1 text-2xl font-bold text-ff-parchment">{j.discovery_remaining}</div>
          <div className="text-xs text-ff-muted">free campaigns remaining</div>
        </div>
        <div className="ff-panel rounded-md p-4" data-testid="journey-explorer">
          <div className="text-xs uppercase tracking-widest text-ff-brass">Explorer Rate</div>
          <div className="mt-1 text-2xl font-bold text-ff-parchment">{j.explorer_rate_remaining}</div>
          <div className="text-xs text-ff-muted">at {j.structure?.explorer_rate_pct}% of standard price</div>
        </div>
        <div className="ff-panel rounded-md p-4" data-testid="journey-standard">
          <div className="text-xs uppercase tracking-widest text-ff-brass">Standard Access</div>
          <div className="mt-1 text-2xl font-bold text-ff-parchment">{j.standard_remaining}</div>
          <div className="text-xs text-ff-muted">campaigns remaining</div>
        </div>
      </div>

      <div className="ff-panel rounded-md p-5" data-testid="journey-sponsored">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-ff-gold">
          {j.sponsored_frontier_eligible ? <Sparkle size={20} weight="duotone" /> : <Lock size={20} weight="duotone" />}
          Sponsored Frontier
        </h2>
        <p className="mt-2 text-sm text-ff-parchment/90">{j.messages?.sponsored_frontier}</p>
        <p className="mt-2 text-xs text-ff-muted" data-testid="journey-sponsored-progress">
          Eligibility progress: {j.sponsored_frontier_progress?.completed} / {j.sponsored_frontier_progress?.required} qualifying campaigns
          {j.sponsored_frontier_eligible ? " · Eligible" : " · Not yet eligible"}
        </p>
        <p className="mt-1 text-xs text-ff-muted">Eligibility does not guarantee a sponsored campaign is available.</p>
      </div>

      {j.acquired_campaigns?.length > 0 && (
        <div className="ff-panel rounded-md p-5" data-testid="journey-acquired">
          <h2 className="text-lg font-semibold text-ff-gold">Campaigns you've experienced</h2>
          <ul className="mt-3 space-y-1.5">
            {j.acquired_campaigns.map((c) => (
              <li key={c.expedition_id} className="flex items-center gap-2 text-sm text-ff-parchment/90">
                <CheckCircle size={16} weight="duotone" className="text-ff-brass" /> {c.name || c.expedition_id}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="rounded-md border border-ff-brass/30 bg-ff-bg2 p-5" data-testid="journey-charged-note">
        <h3 className="text-sm font-semibold text-ff-parchment">What is and isn't charged</h3>
        <ul className="mt-2 space-y-1 text-xs text-ff-muted">
          {(j.notes || []).map((n, i) => <li key={i}>◆ {n}</li>)}
          <li className="text-ff-brass">◆ Payments are currently disabled — everything is free during this preview.</li>
        </ul>
      </div>
    </div>
  );
}
